import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'

export interface FormInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: ReactNode
  error?: string | boolean
  sizeVariant?: 'sm' | 'md' | 'lg'
  size?: 'sm' | 'md' | 'lg'
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  containerClassName?: string
}

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      error,
      sizeVariant = 'md',
      size,
      leftIcon,
      rightIcon,
      className = '',
      containerClassName = '',
      disabled,
      type = 'text',
      ...props
    },
    ref
  ) => {
    const hasError = Boolean(error)
    const effectiveSize = (typeof size === 'string' ? size : sizeVariant) as 'sm' | 'md' | 'lg'

    const sizeClasses = {
      sm: 'h-9 text-xs px-3',
      md: 'h-[46px] text-sm px-3.5',
      lg: 'h-12 text-sm px-4',
    }[effectiveSize] || 'h-[46px] text-sm px-3.5'

    const paddingLeft = leftIcon ? (effectiveSize === 'sm' ? 'pl-9' : 'pl-10') : ''
    const paddingRight = rightIcon ? (effectiveSize === 'sm' ? 'pr-9' : 'pr-10') : ''

    const stateClasses = hasError
      ? 'border-red-500 bg-red-50/20 text-neutral-900 focus:border-red-500 focus:ring-2 focus:ring-red-200'
      : 'border-slate-200 bg-white text-neutral-900 hover:border-slate-300 focus:border-[#146971] focus:ring-2 focus:ring-[#146971]/20 focus:bg-white'

    const inputElement = (
      <input
        ref={ref}
        type={type}
        disabled={disabled}
        className={`w-full rounded-lg border font-medium outline-none transition-all duration-150 ease-in-out placeholder:text-[#98a2b3] placeholder:text-[13px] disabled:cursor-not-allowed disabled:bg-[#f9fafb] disabled:text-[#667085] disabled:border-[#e4e7ec] ${sizeClasses} ${paddingLeft} ${paddingRight} ${stateClasses} ${className}`}
        {...props}
      />
    )

    return (
      <div className={`relative flex items-center w-full ${containerClassName}`}>
        {leftIcon ? (
          <div className="pointer-events-none absolute left-3.5 flex items-center justify-center text-neutral-400">
            {leftIcon}
          </div>
        ) : null}
        {inputElement}
        {rightIcon ? (
          <div className="absolute right-3.5 flex items-center justify-center text-neutral-400">
            {rightIcon}
          </div>
        ) : null}
      </div>
    )
  }
)

FormInput.displayName = 'FormInput'

export const Input = FormInput
export type InputProps = FormInputProps

